import type { AppContext } from "../../app/app";
import {
  buildBackup,
  parseBackup,
  restoreBackup,
} from "../../data/backup";
import {
  type LlmMode,
  type LlmSettings,
  loadSettings,
  saveSettings,
} from "../../llm/settings";
import { applyPacks, type Pack } from "../../data/packs";
import { confirmDeleteAll } from "../danger";
import { readStorageEnv, requestPersistenceAgain } from "../storage";
import { renderAbout } from "../about";
import { el } from "../dom";
import {
  canonicalSelect,
  readCanonical,
  renderCanonicalField,
  renderPackChooser,
  renderProposer,
} from "../dimension-tools";

// Settings: choose how the app reaches the model. Proxy is the default on the
// hosted subdomain; BYO-key keeps everything off STRAD's infrastructure. The
// key is stored only in this browser's IndexedDB.
export async function renderSettings(ctx: AppContext): Promise<HTMLElement> {
  const settings = await loadSettings(ctx.repo);
  const root = el("section", {});

  const modeProxy = radio("mode", "proxy", settings.mode === "proxy");
  const modeByok = radio("mode", "byok", settings.mode === "byok");
  const proxyUrl = el("input", {
    class: "input",
    type: "url",
    placeholder: "https://trust-lens-proxy.example.workers.dev",
    value: settings.proxyUrl,
  });
  const apiKey = el("input", {
    class: "input",
    type: "password",
    placeholder: "sk-ant-... (stored only in this browser)",
    value: settings.apiKey,
  });
  const model = el("input", {
    class: "input",
    type: "text",
    value: settings.model,
  });
  const status = el("span", { class: "remaining" });

  const collect = (): LlmSettings => ({
    mode: (modeByok.checked ? "byok" : "proxy") as LlmMode,
    proxyUrl: proxyUrl.value.trim(),
    apiKey: apiKey.value.trim(),
    model: model.value.trim() || settings.model,
  });

  const save = el("button", {
    class: "btn btn--primary",
    text: "Save settings",
    on: {
      click: async () => {
        await saveSettings(ctx.repo, collect());
        status.textContent = "Saved.";
        status.classList.remove("is-over");
      },
    },
  });

  const test = el("button", {
    class: "btn btn--ghost",
    text: "Test connection",
    on: {
      click: async () => {
        await saveSettings(ctx.repo, collect());
        status.textContent = "Testing...";
        status.classList.remove("is-over");
        try {
          await ctx.provider.complete({
            system: "Reply with the single word: ready.",
            messages: [{ role: "user", content: "ping" }],
            maxTokens: 8,
          });
          status.textContent = "Connected.";
        } catch (e) {
          status.textContent =
            e instanceof Error ? e.message : "Could not reach the model.";
          status.classList.add("is-over");
        }
      },
    },
  });

  root.append(
    el("span", { class: "label", text: "Settings" }),
    el("h1", { text: "Model access" }),
    el("p", {
      class: "lede",
      text:
        "Storage and processing are separate. STORAGE: your people, tags and " +
        "rationales stay only in this browser; there is no data server. " +
        "PROCESSING: Sharpen, Surface and Assemble transmit your own tags and " +
        "rationales, which can name other people, to the cloud model. Nothing " +
        "is stored on a server; choose below how that call is made.",
    }),
    el("p", {
      class: "lede explainer",
      text:
        "AI processing. The AI features send your notes to Anthropic's Claude " +
        "API to produce analysis. STRAD shared key: requests pass through " +
        "STRAD's proxy (which stores nothing) and run on STRAD's account. Your " +
        "own key: requests go directly from your browser using your Anthropic " +
        "key. Either way, your notes are not stored on any STRAD server.",
    }),
    field(
      "Mode",
      el(
        "div",
        { class: "radio-row" },
        label(modeProxy, "STRAD proxy (default)"),
        label(modeByok, "Bring your own key"),
      ),
    ),
    field("Proxy URL", proxyUrl),
    field("API key", apiKey),
    field("Model", model),
    el("div", { class: "settings__actions" }, save, test, status),
    el(
      "p",
      { class: "lede" },
      "How your data is handled: ",
      el("button", {
        class: "link-inline",
        type: "button",
        text: "read the Privacy notice",
        on: { click: () => ctx.navigate({ name: "privacy" }) },
      }),
      ".",
    ),
    await dimensionsSection(ctx),
    await storageSection(ctx),
    dataSection(ctx),
    el("div", { class: "settings__data" }, renderAbout()),
    dangerSection(ctx),
  );
  return root;
}

// Current persistent-storage status, with a re-request action when not granted.
async function storageSection(ctx: AppContext): Promise<HTMLElement> {
  const env = await readStorageEnv();
  const root = el("div", { class: "settings__data" });
  root.append(
    el("span", { class: "label", text: "Storage" }),
    el("p", {
      class: "lede data-note",
      text: env.persisted
        ? "Persistent. Your browser has granted persistent storage, so it will not clear Trust Lens data to reclaim space. Your data still lives only in this browser; export a backup to keep it or move device."
        : env.supported
          ? "Not persistent. Your browser may clear Trust Lens data under storage pressure. Export a backup to be safe, or ask your browser to store it persistently."
          : "Not persistent. This browser does not support persistent storage, so it may clear Trust Lens data under storage pressure. Export a backup to be safe.",
    }),
  );
  if (!env.persisted && env.supported) {
    root.append(
      el("button", {
        class: "btn btn--ghost",
        text: "Request again",
        on: { click: () => void requestPersistenceAgain(ctx) },
      }),
    );
  }
  return root;
}

// Destructive actions, deliberately set apart from the routine ones above so a
// reset is never a stray click. The actual erase is gated behind a typed-word
// confirmation (see confirmDeleteAll).
function dangerSection(ctx: AppContext): HTMLElement {
  return el(
    "div",
    { class: "settings__danger" },
    el("span", { class: "label", text: "Danger zone" }),
    el("p", {
      class: "lede data-note",
      text:
        "Delete everything in this browser: all people, ratings, events, " +
        "decisions, domain flags and settings. This cannot be undone. Export a " +
        "backup first if you might want the data back.",
    }),
    el("button", {
      class: "btn btn--danger",
      text: "Delete all data",
      on: { click: () => confirmDeleteAll(ctx) },
    }),
  );
}

// Manage the qualities people are rated on: edit, add, remove (warned when a
// quality already carries ratings), swap to a curated pack, or propose a set
// from a role. Dimensions stay fully editable; canonical_key is untouched.
async function dimensionsSection(ctx: AppContext): Promise<HTMLElement> {
  const [dimensions, tags] = await Promise.all([
    ctx.repo.listDimensions(),
    ctx.repo.listTags({ viewer_id: ctx.viewer }),
  ]);
  const tagCount = new Map<string, number>();
  for (const t of tags) tagCount.set(t.dimension_id, (tagCount.get(t.dimension_id) ?? 0) + 1);

  const root = el("div", { class: "settings__data" });
  root.append(
    el("span", { class: "label", text: "Qualities" }),
    el("p", {
      class: "lede data-note",
      text:
        "The qualities you rate people on. Rename or redefine them, add your " +
        "own, or replace the set. Removing a quality that has ratings deletes " +
        "those ratings.",
    }),
  );

  const list = el("div", { class: "dim-manage" });
  for (const d of dimensions) {
    const count = tagCount.get(d.id) ?? 0;
    const name = el("input", { class: "input", type: "text", value: d.name });
    const definition = el("input", {
      class: "input",
      type: "text",
      value: d.definition,
      placeholder: "One-line definition (optional)",
    });
    const mapping = canonicalSelect(d.canonical_key);
    const save = el("button", {
      class: "btn btn--ghost",
      text: "Save",
      on: {
        click: async () => {
          await ctx.repo.updateDimension(d.id, {
            name: name.value.trim() || d.name,
            definition: definition.value.trim(),
            canonical_key: readCanonical(mapping),
          });
          ctx.refresh();
        },
      },
    });
    const remove = el("button", {
      class: "btn btn--danger",
      text: "Remove",
      on: {
        click: async () => {
          const warn =
            count > 0
              ? `Remove "${d.name}"? This deletes ${count} rating${count === 1 ? "" : "s"} on it.`
              : `Remove "${d.name}"?`;
          if (!confirm(warn)) return;
          await ctx.repo.deleteDimension(d.id);
          ctx.refresh();
        },
      },
    });
    list.append(
      el(
        "div",
        { class: "dim-manage__row" },
        el(
          "div",
          { class: "dim-manage__fields" },
          name,
          definition,
          el("div", { class: "canonical-field" }, el("span", { class: "label", text: "Maps to" }), mapping),
        ),
        el(
          "div",
          { class: "dim-manage__meta" },
          el("span", { class: "label", text: count === 0 ? "no ratings" : `${count} rated` }),
          save,
          remove,
        ),
      ),
    );
  }
  root.append(list);

  // Add a single quality, mapping it on create (picklist + optional suggest).
  const newName = el("input", { class: "input", type: "text", placeholder: "New quality" });
  const newDef = el("input", { class: "input", type: "text", placeholder: "One-line definition (optional)" });
  const addErr = el("span", { class: "error" });
  const { select: newKey, row: keyRow } = renderCanonicalField(ctx, null, () => ({
    name: newName.value.trim(),
    definition: newDef.value.trim(),
  }));
  const add = el("button", {
    class: "btn btn--primary",
    text: "Add quality",
    on: {
      click: async () => {
        const nm = newName.value.trim();
        if (!nm) {
          addErr.textContent = "Name the quality first.";
          return;
        }
        await ctx.repo.addDimension(nm, newDef.value.trim(), readCanonical(newKey));
        ctx.refresh();
      },
    },
  });
  root.append(
    el(
      "div",
      { class: "dim-manage__add" },
      el("div", { class: "dim-manage__add-fields" }, newName, newDef, keyRow),
      add,
    ),
    addErr,
  );

  // Replace the whole set with one or more curated packs (warned when ratings
  // exist, since replacing removes their tags).
  const hasTags = tags.length > 0;
  const replaceWith = async (label: string, run: () => Promise<void>) => {
    if (hasTags && !confirm(`Replace your qualities with the ${label} set? This removes all current qualities and their ratings.`)) {
      return;
    }
    await run();
    ctx.refresh();
  };
  const applyChosen = (packs: Pack[]) => {
    const label = packs.map((p) => p.label).join(" + ");
    void replaceWith(label, () => applyPacks(ctx.repo, packs));
  };
  root.append(
    el("span", { class: "label", text: "Replace with one or more sets" }),
    renderPackChooser(applyChosen),
    el("span", { class: "label", text: "Propose from your role" }),
    renderProposer(ctx, (dims) =>
      replaceWith("proposed", async () => {
        for (const d of await ctx.repo.listDimensions()) await ctx.repo.deleteDimension(d.id);
        for (const dim of dims) await ctx.repo.addDimension(dim.name, dim.definition, dim.canonical_key);
      }),
    ),
  );
  return root;
}

// Backup / restore. The export holds judgement data only (no key). Import
// replaces what is here, so it is confirmed first.
function dataSection(ctx: AppContext): HTMLElement {
  const status = el("span", { class: "remaining" });

  const download = el("button", {
    class: "btn btn--ghost",
    text: "Download backup",
    on: {
      click: async () => {
        const file = await buildBackup(ctx.repo, Date.now());
        const stamp = new Date().toISOString().slice(0, 10);
        const blob = new Blob([JSON.stringify(file, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `trust-lens-backup-${stamp}.json`;
        a.click();
        URL.revokeObjectURL(url);
        status.textContent = "Backup downloaded.";
        status.classList.remove("is-over");
      },
    },
  });

  const fileInput = el("input", { type: "file" });
  fileInput.accept = ".json,application/json";
  fileInput.style.display = "none";
  fileInput.addEventListener("change", async () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    try {
      const backup = parseBackup(await f.text());
      const counts = `${backup.data.people.length} people, ${backup.data.tags.length} tags`;
      if (!confirm(`This replaces your current data with ${counts}. Continue?`)) {
        return;
      }
      await restoreBackup(ctx.repo, backup);
      status.textContent = `Imported ${counts}.`;
      status.classList.remove("is-over");
    } catch (e) {
      status.textContent =
        e instanceof Error ? e.message : "Could not import that file.";
      status.classList.add("is-over");
    } finally {
      fileInput.value = "";
    }
  });

  const importBtn = el("button", {
    class: "btn btn--ghost",
    text: "Import backup",
    on: { click: () => fileInput.click() },
  });

  return el(
    "div",
    { class: "settings__data" },
    el("span", { class: "label", text: "Your data" }),
    el("p", {
      class: "lede data-note",
      text:
        "Your people, tags and rationales live only in this browser. If you " +
        "clear your browser data they are lost. Export a backup to keep them " +
        "or move to another browser or device. Importing replaces the data " +
        "here; settings and any key are not included.",
    }),
    el("div", { class: "settings__actions" }, download, importBtn, fileInput, status),
  );
}

function radio(name: string, value: string, checked: boolean): HTMLInputElement {
  const input = el("input", { type: "radio", attrs: { name, value } });
  input.checked = checked;
  return input;
}

function label(input: HTMLElement, text: string): HTMLElement {
  return el("label", { class: "radio" }, input, el("span", { text }));
}

function field(name: string, control: HTMLElement): HTMLElement {
  return el(
    "div",
    { class: "settings__field" },
    el("span", { class: "label", text: name }),
    control,
  );
}
